<template>
  <div class="flex flex-col gap-3">
    <label class="font-semibold">{{ label }}</label>

    <Multiselect
      v-model="selectedParents"
      @change="handleParentsChange"
      mode="tags"
      :options="parentOptions"
      :object="true"
      :trackBy="'value'"
      :label="'label'"
      :valueProp="'value'"
      placeholder=""
    />

    <template v-for="(childList, slug) in childOptionsMap" :key="slug">
      <div class="flex flex-col gap-1 mt-2">
        <label class="text-sm font-semibold">{{ childLabels[slug] }}</label>

        <Multiselect
          v-model="selectedChildren[slug]"
          mode="tags"
          :options="childList"
          @change="val => handleChildChange(slug, val)"
          placeholder=""
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import Multiselect from '@vueform/multiselect';
import { useFilterStore } from '@/store/filterStore.js';
import { getNestedSelectValues } from '@/helpers/nestedSelectCache';

const filterStore = useFilterStore();

const props = defineProps({
  label: String,
  property: Number, // parent property id (Марка)
  defaultMultiValues: Array,
  dataType: Number,
});

const selectedParents = ref([]);
const selectedChildren = ref({}); // slug -> selected child values (array)
const parentOptions = ref([]);
const childOptionsMap = ref({}); // slug -> [{value,label}]
const childLabels = ref({}); // slug -> string
const childPropertyBySlug = ref({}); // slug -> child_property.id

function normalizeIds(arr) {
  return (Array.isArray(arr) ? arr : [])
    .map(v => (typeof v === 'object' && v !== null ? v.value : v))
    .map(v => parseInt(v))
    .filter(v => !Number.isNaN(v));
}

// ✅ ВАЖНО: option хранит slug/children/children_property прямо в себе (не через raw)
const fetchParentOptions = async () => {
  if (parentOptions.value.length) return;

  const data = await getNestedSelectValues(props.property);

  parentOptions.value = data.map(item => ({
    value: item.id,
    label: item.name,

    // ✅ критично для стабильности v-model
    slug: item.slug,
    children: item.children || [],
    children_property: item.children_property || null,
  }));
};

/**
 * Храним группировку в отдельном параметре multi_nested (JSON string).
 * Backend должен применить: (Toyota AND (4Runner OR Allex)) OR (Lexus AND (CT OR CT200h)) ...
 */
function syncMultiNestedToStore() {
  const groups = [];

  for (const parent of selectedParents.value) {
    const slug = parent?.slug;
    if (!slug) continue;

    const parentId = parent.value;

    const childPropId = parent?.children_property?.id || childPropertyBySlug.value[slug] || null;

    if (childPropId) childPropertyBySlug.value[slug] = childPropId;

    const childSelected = normalizeIds(selectedChildren.value[slug]);

    groups.push({
      parent: parentId,
      child_property: childPropId,
      children: childSelected,
    });
  }

  // ✅ если групп нет — НЕ отправляем "[]", а чистим фильтр
  filterStore.updateFilter({
    type: 'multi_nested',
    property: props.property,
    value: groups.length ? JSON.stringify(groups) : '',
  });
}

const handleParentsChange = async parents => {
  // ✅ обязательно синхроним выбранных родителей
  selectedParents.value = Array.isArray(parents) ? parents : [];

  // если всё снято
  if (!selectedParents.value.length) {
    selectedChildren.value = {};
    childOptionsMap.value = {};
    childLabels.value = {};
    childPropertyBySlug.value = {};

    filterStore.updateFilter({
      type: 'multi_nested',
      property: props.property,
      value: '',
    });

    await filterStore.fetchDynamicCount({ mode: 'include' });
    return;
  }

  // 1) удалить UI для снятых родителей
  const currentParentIds = selectedParents.value.map(p => p.value);

  for (const slug of Object.keys(childOptionsMap.value)) {
    const parent = parentOptions.value.find(p => p.slug === slug);
    if (parent && !currentParentIds.includes(parent.value)) {
      delete childOptionsMap.value[slug];
      delete childLabels.value[slug];
      delete selectedChildren.value[slug];
      delete childPropertyBySlug.value[slug];
    }
  }

  // 2) добавить UI для новых родителей
  for (const parent of selectedParents.value) {
    const slug = parent.slug;

    const childPropId = parent?.children_property?.id;
    if (childPropId) childPropertyBySlug.value[slug] = childPropId;

    if (!childOptionsMap.value[slug]) {
      childOptionsMap.value[slug] = (parent.children || []).map(child => ({
        value: child.id,
        label: child?.name?.trim() || `ID ${child.id}`,
      }));

      childLabels.value[slug] = `${parent.children_property?.name || ''} | ${parent.label}`;
      selectedChildren.value[slug] = [];
    }
  }

  // 3) синхроним группы в store
  syncMultiNestedToStore();

  // ✅ total count
  await filterStore.fetchDynamicCount({ mode: 'include' });
};

const handleChildChange = async (slug, newValues) => {
  selectedChildren.value[slug] = Array.isArray(newValues) ? newValues : [];

  syncMultiNestedToStore();

  await filterStore.fetchDynamicCount({ mode: 'include' });
};

onMounted(async () => {
  await fetchParentOptions();

  if (props.defaultMultiValues?.length) {
    // defaultMultiValues содержит ID родителей
    const defaultParents = parentOptions.value.filter(p =>
      props.defaultMultiValues.includes(p.value),
    );
    selectedParents.value = defaultParents;
    await handleParentsChange(defaultParents);
  }
});
</script>

<style scoped>
@import '@vueform/multiselect/themes/tailwind.css';
</style>
